import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Lock, ShieldCheck, User } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { BrandMark } from '@/components/Brand';
import { Button, Input, Label } from '@/components/ui';
import { ThemeToggle } from '@/lib/theme';

const loginSchema = z.object({
  username: z.string().min(3, 'Nama pengguna minimal 3 karakter.'),
  password: z.string().min(8, 'Kata sandi minimal 8 karakter.'),
});
type LoginValues = z.infer<typeof loginSchema>;

const FEATURES = [
  { icon: ShieldCheck, title: 'Aman & terpercaya', text: 'Akses berbasis peran dengan audit aktivitas.' },
  { icon: User, title: 'Data kependudukan', text: 'Big data warga, DPS, kelahiran & perpindahan.' },
  { icon: Lock, title: 'Terpusat', text: 'Layanan surat, pengaduan, UMKM, dan infrastruktur.' },
];

export function LoginPage() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  if (!loading && user) {
    return <Navigate to="/app" replace />;
  }

  async function onSubmit(values: LoginValues) {
    setSubmitting(true);
    setError(null);
    try {
      await login(values.username, values.password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/masuk' ? from : '/app', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal masuk.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-tirta-950 p-10 text-tirta-100 lg:flex">
        <div className="pointer-events-none absolute inset-0 bg-grid opacity-[0.07]" />
        <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-tirta-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />

        <div className="relative flex items-center gap-2.5">
          <BrandMark className="h-11 w-11" />
          <span className="text-lg font-bold text-white">TIRTA SMART</span>
        </div>

        <div className="relative max-w-md">
          <h1 className="text-3xl font-bold leading-tight text-white">
            Satu Desa, Satu Sistem, Maju Bersama.
          </h1>
          <p className="mt-4 text-sm text-tirta-200/80">
            Sistem terintegrasi Pemerintah Desa Tirtasari — layanan surat, data desa, pengaduan
            warga, UMKM, infrastruktur, keamanan, dan kesehatan dalam satu platform.
          </p>
          <ul className="mt-8 space-y-4">
            {FEATURES.map((feature) => (
              <li key={feature.title} className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-tirta-200 ring-1 ring-white/10">
                  <feature.icon className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{feature.title}</span>
                  <span className="block text-xs text-tirta-200/70">{feature.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-tirta-300/60">
          Desa Tirtasari · Kabupaten Karawang · Jawa Barat
        </p>
      </div>

      {/* Form panel */}
      <div className="relative flex items-center justify-center p-6">
        <div className="absolute right-4 top-4">
          <ThemeToggle />
        </div>
        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:text-left">
            <BrandMark className="mb-4 h-11 w-11 lg:hidden" />
            <h2 className="text-2xl font-bold tracking-tight">Masuk ke akun Anda</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Gunakan akun yang diberikan administrator desa.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="username">Nama pengguna</Label>
              <Input
                id="username"
                autoComplete="username"
                autoFocus
                placeholder="mis. operator"
                aria-invalid={Boolean(errors.username)}
                {...register('username')}
              />
              {errors.username ? (
                <p className="text-xs text-destructive">{errors.username.message}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Kata sandi</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="pr-10"
                  aria-invalid={Boolean(errors.password)}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password ? (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              ) : null}
            </div>

            {error ? (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}

            <Button type="submit" className="w-full" size="lg" loading={submitting}>
              Masuk
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            <Link to="/" className="transition-colors hover:text-foreground">
              ← Kembali ke portal publik
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
